import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	EventEmitter,
	inject,
	Input,
	linkedSignal,
	Output,
	signal,
} from '@angular/core';
import { PeriodType, periodTypes } from '@app/domain/gql/types';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { DateIntervalLabelService } from '@app/core/services/date-interval-label.service';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { OverlayModule } from '@angular/cdk/overlay';

@Component({
	selector: 'applied-filter-button',
	templateUrl: './date-interval-filter.component.html',
	styleUrls: ['./date-interval-filter.component.scss'],
	imports: [CommonModule, MatIconModule, MatListModule, NgxTolgeeModule, OverlayModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateIntervalFilterComponent implements ICmmsFilterComponent {
	private readonly labelService = inject(DateIntervalLabelService);

	// Inputs are not used, but kept to fulfill interface
	@Input() data?: {
		preselectedValue?: null;
		activePage?: CmmsPages;
		columnName?: string;
		options?: { [key: string]: null };
	};
	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	options = periodTypes;
	granularity = signal<PeriodType | null>(null);
	offset = linkedSignal(() => {
		this.granularity();
		return 0;
	});
	hasSelectedGranularity = computed(() => this.granularity() !== null);
	label = computed(() => {
		if (!this.hasSelectedGranularity()) {
			return this.labelService.default();
		}
		return this.labelService.label({
			granularity: this.granularity(),
			offset: this.offset(),
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
	});
	granularityMenuOpen = signal(false);

	constructor() {
		effect(() => {
			if (!this.hasSelectedGranularity()) {
				return;
			}
			this.filterValueChanged.emit({
				columnName: 'dateIntervalFilter',
				label: this.label(),
				data: {
					granularity: this.granularity(),
					offset: this.offset(),
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				},
			});
		});
	}

	private addToOffset(value: number) {
		if (!this.hasSelectedGranularity()) {
			return;
		}
		this.offset.update(current_value => current_value + value);
	}

	setGranularity(granularity: PeriodType) {
		this.granularity.set(granularity);
	}

	increment() {
		this.addToOffset(1);
	}
	decrement() {
		this.addToOffset(-1);
	}
}
